import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Modal } from "semantic-ui-react";
import {
  formatPatternSizeLabel,
  getOrderedPatternSizes,
  normalizePatternSize,
} from "../../utils/patternSize";
import styles from "./SizeSelectionModal.module.scss";

type Props = {
  open: boolean;
  sizes: string[];
  multiple?: boolean;
  unavailableSizes?: string[];
  initialSelectedSizes?: string[];
  patternName?: string;
  patternImageUrl?: string;
  onClose: () => void;
  onConfirm: (sizes: string[]) => void;
};

const SizeSelectionModal: React.FC<Props> = ({
  open,
  sizes,
  multiple = false,
  unavailableSizes = [],
  initialSelectedSizes,
  patternName = "",
  patternImageUrl = "",
  onClose,
  onConfirm,
}) => {
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const orderedSizes = useMemo(() => getOrderedPatternSizes(sizes), [sizes]);
  const unavailableSizeSet = useMemo(
    () =>
      new Set(
        (Array.isArray(unavailableSizes) ? unavailableSizes : [])
          .filter((size): size is string => typeof size === "string")
          .map((size) => normalizePatternSize(size))
      ),
    [unavailableSizes]
  );

  useEffect(() => {
    if (open) {
      const requestedSizes = Array.from(
        new Set(
          (Array.isArray(initialSelectedSizes) ? initialSelectedSizes : [])
            .filter((size): size is string => typeof size === "string")
            .map((size) => normalizePatternSize(size))
            .filter((size) => orderedSizes.includes(size) && !unavailableSizeSet.has(size))
        )
      );

      if (multiple) {
        setSelectedSizes(requestedSizes);
      } else {
        setSelectedSizes(requestedSizes.length > 0 ? [requestedSizes[0]] : []);
      }
    }
  }, [open, initialSelectedSizes, multiple, orderedSizes, unavailableSizeSet]);

  const toggleSize = (size: string) => {
    if (unavailableSizeSet.has(size)) {
      return;
    }

    setSelectedSizes((previous) => {
      if (multiple) {
        return previous.includes(size)
          ? previous.filter((value) => value !== size)
          : [...previous, size];
      }

      return previous.includes(size) ? [] : [size];
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeIcon
      size="tiny"
      dimmer="inverted"
      className={styles.modal}
    >
      <Modal.Content className={styles.content}>
        <div className={styles.layout}>
          <div className={styles.selectionColumn}>
            <h3 className={styles.title}>
              <FormattedMessage
                id="cart.sizePicker.title"
                defaultMessage="Select a size"
              />
            </h3>
            <p className={styles.description}>
              <FormattedMessage
                id="cart.sizePicker.description"
                defaultMessage="Choose the pattern size you want to add to cart."
              />
            </p>

            <div className={styles.sizeGrid}>
              {orderedSizes.map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => toggleSize(size)}
                  disabled={unavailableSizeSet.has(size)}
                  className={`${styles.sizeChip} ${
                    selectedSizes.includes(size) ? styles.sizeChipActive : ""
                  } ${
                    unavailableSizeSet.has(size) ? styles.sizeChipUnavailable : ""
                  }`.trim()}
                >
                  {formatPatternSizeLabel(size)}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.actionsColumn}>
            {(!!patternImageUrl || !!patternName) && (
              <div className={styles.patternPreview}>
                {!!patternImageUrl && (
                  <img
                    src={patternImageUrl}
                    alt={patternName || "Pattern"}
                    className={styles.patternImage}
                  />
                )}
                {!!patternName && <p className={styles.patternName}>{patternName}</p>}
              </div>
            )}

            <div className={styles.actions}>
              <button type="button" onClick={onClose} className={styles.cancelButton}>
                <FormattedMessage id="cart.sizePicker.cancel" defaultMessage="Cancel" />
              </button>
              <button
                type="button"
                onClick={() => onConfirm(selectedSizes)}
                className={styles.confirmButton}
                disabled={selectedSizes.length === 0}
              >
                <FormattedMessage
                  id="cart.sizePicker.confirm"
                  defaultMessage="Add to cart"
                />
              </button>
            </div>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
};

export default SizeSelectionModal;
